import { classes } from "./classes.js";

/**
 * `P0-UIK-003`: invariant **I20**, rendered.
 *
 * > **A terminal branch never shows a future stage.** The stepper renders the
 * > path travelled plus the terminal node.
 *
 * **Danger sentence.** A client whose file was refused, looking at a stepper
 * that still shows `Offre retenue` and `Décaissé` greyed out ahead of them, is
 * being shown a future that no longer exists. They will wait for it. They will
 * telephone about it. I20 is not a rendering preference , it is the difference
 * between a refusal a client understands and a refusal they keep hoping past.
 *
 * **This component receives the path; it does not compute it.** The rule lives
 * in `cheminStepper` in `@hakili/domain-types`, where it is already proved ,
 * every mainline stage travelled, the terminal node when there is one, and
 * ZERO future nodes , and 9.3.1 forbids this package from importing it. So the
 * app calls `cheminStepper(etape)` and passes the result here. That split is
 * why the invariant has one implementation and one test rather than one per
 * app: a `Stepper` that took a current stage and derived the path would be a
 * second implementation of I20, in the layer least able to test it.
 *
 * The type makes the shape hard to misuse anyway: there is no `future` prop to
 * pass, so a caller cannot render a node the path does not contain.
 *
 * **`P1-REST` (20 October 2026): an optional `ahead` list, for a file still moving.** The console's chargée wants to
 * see where a live file is going. `ahead` is drawn after the current node, in grey with a hollow mark, never as the
 * current one; a caller hands it ONLY for a file on the main line , `fullStepperPath` returns it empty on a terminal
 * branch, so `I20` keeps its one implementation. Absent or empty, the markup is what it always was.
 */

export interface StepperNode {
  readonly id: string;
  /** What the node prints. From `@hakili/domain-types`, never written here. */
  readonly label: string;
  /** True for the last node of a terminal branch, which renders as an end. */
  readonly terminal?: boolean;
}

export interface StepperProps {
  /** The path travelled, in order, ending at the current node. */
  readonly path: readonly StepperNode[];
  /** `P1-REST`: the stages still ahead of a live file, in order; never handed for a terminal branch. */
  readonly ahead?: readonly StepperNode[];
  readonly className?: string;
}

export function Stepper({ path, ahead = [], className }: StepperProps): React.JSX.Element {
  return (
    <ol className={classes("flex flex-wrap items-center gap-2", className)}>
      {path.map((node, index) => {
        const current = index === path.length - 1;
        const tone = node.terminal === true ? "bg-rose-50 text-rose-700" : "bg-emerald-50 text-emerald-700";
        return (
          <li key={node.id} className="flex items-center gap-2">
            <span
              aria-current={current ? "step" : undefined}
              className={classes(
                "rounded-full px-2 py-1 text-xs font-medium",
                current ? tone : "bg-gray-50 text-gray-500",
              )}
            >
              {node.label}
            </span>
            {index === path.length - 1 && ahead.length === 0 ? null : (
              <span className="text-xs text-gray-400">›</span>
            )}
          </li>
        );
      })}
      {ahead.map((node, index) => (
        <li key={node.id} data-ahead="true" className="flex items-center gap-2">
          <span className="rounded-full border border-dashed border-gray-200 px-2 py-1 text-xs font-medium text-gray-500">
            <span aria-hidden="true">○ </span>
            {node.label}
          </span>
          {index === ahead.length - 1 ? null : <span className="text-xs text-gray-400">›</span>}
        </li>
      ))}
    </ol>
  );
}
